import { supabase } from '../supabase';
import { buildFullAddress } from '../utils/routePlannerUtils';
import type { Client } from '../types';
import type { GeocodedLead } from '../types/visitRoute';

// Cache in-memory per la sessione (evita ri-geocodifica)
const geocodeCache = new Map<string, { lat: number; lng: number } | null>();

/**
 * Verifica se un client ha bisogno di geocodifica (o ri-geocodifica)
 */
export function needsGeocoding(client: Client): boolean {
  // Ha già coordinate salvate in DB
  if (client.latitude != null && client.longitude != null) return false;
  // Ha almeno una città per poter geocodificare
  if (!client.city?.trim() && !client.country?.trim()) return false;
  return true;
}

/**
 * Verifica se un client deve essere ri-geocodificato (indirizzo cambiato)
 */
export function shouldRegeocode(client: Client): boolean {
  if (!client.geocoded_at) return true;
  // Se ha coordinate ma nessun indirizzo completo, potrebbe aver aggiornato l'indirizzo
  // Per ora non implementiamo change detection, il re-geocode avviene solo su richiesta
  return false;
}

/**
 * Geocodifica un singolo client usando Google Geocoding API
 */
export async function geocodeClient(
  client: Client
): Promise<{ lat: number; lng: number } | null> {
  // Check cache
  if (geocodeCache.has(client.id)) {
    return geocodeCache.get(client.id)!;
  }

  // Se ha già coordinate nel DB
  if (client.latitude != null && client.longitude != null) {
    const coords = { lat: client.latitude, lng: client.longitude };
    geocodeCache.set(client.id, coords);
    return coords;
  }

  const { address } = buildFullAddress(client);
  if (!address) {
    geocodeCache.set(client.id, null);
    return null;
  }

  // Controlla che Google Maps sia caricato
  if (!(window as any).google?.maps?.Geocoder) {
    console.warn('[geocodingService] Google Maps non caricato');
    return null;
  }

  return new Promise(resolve => {
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ address }, (results, status) => {
      if (status === 'OK' && results && results[0]) {
        const loc = results[0].geometry.location;
        const coords = { lat: loc.lat(), lng: loc.lng() };
        geocodeCache.set(client.id, coords);
        resolve(coords);
      } else {
        geocodeCache.set(client.id, null);
        resolve(null);
      }
    });
  });
}

/**
 * Salva le coordinate geocodificate nel DB
 */
export async function saveGeocodeToDB(
  clientId: string,
  lat: number,
  lng: number
): Promise<void> {
  const { error } = await supabase
    .from('clients')
    .update({
      latitude: lat,
      longitude: lng,
      geocoded_at: new Date().toISOString(),
    })
    .eq('id', clientId);

  if (error) {
    console.error('[geocodingService] Errore salvataggio geocode:', error);
  }
}

/**
 * Geocodifica un batch di client in sequenza, con callback per il progresso.
 * Salva automaticamente le coordinate nel DB.
 */
export async function batchGeocodeClients(
  clients: Client[],
  onProgress?: (completed: number, total: number) => void,
  signal?: AbortSignal
): Promise<Map<string, { lat: number; lng: number }>> {
  const results = new Map<string, { lat: number; lng: number }>();
  const toGeocode = clients.filter(c => needsGeocoding(c) && !geocodeCache.has(c.id));

  // Aggiungi subito quelli che hanno già coordinate o sono in cache
  for (const client of clients) {
    if (client.latitude != null && client.longitude != null) {
      const coords = { lat: client.latitude, lng: client.longitude };
      results.set(client.id, coords);
      geocodeCache.set(client.id, coords);
    } else if (geocodeCache.has(client.id)) {
      const cached = geocodeCache.get(client.id);
      if (cached) results.set(client.id, cached);
    }
  }

  let completed = 0;
  const total = toGeocode.length;

  for (const client of toGeocode) {
    if (signal?.aborted) break;

    const coords = await geocodeClient(client);
    if (signal?.aborted) break;

    if (coords) {
      results.set(client.id, coords);
      // Salva in DB in background (non blocca)
      saveGeocodeToDB(client.id, coords.lat, coords.lng);
    }

    completed++;
    onProgress?.(completed, total);

    // Delay tra richieste per rispettare i rate limits
    if (completed < total) {
      await new Promise(r => setTimeout(r, 200));
    }
  }

  return results;
}

/**
 * Converte un array di Client con coordinate in GeocodedLead[]
 */
export function clientsToGeocodedLeads(
  clients: Client[],
  coordsMap?: Map<string, { lat: number; lng: number }>
): GeocodedLead[] {
  const result: GeocodedLead[] = [];

  for (const client of clients) {
    let lat: number | undefined;
    let lng: number | undefined;

    // Prima controlla la mappa coordinate fornita
    if (coordsMap?.has(client.id)) {
      const coords = coordsMap.get(client.id)!;
      lat = coords.lat;
      lng = coords.lng;
    }
    // Poi controlla le coordinate salvate nel client
    else if (client.latitude != null && client.longitude != null) {
      lat = client.latitude;
      lng = client.longitude;
    }
    // Infine controlla la cache
    else {
      const cached = geocodeCache.get(client.id);
      if (cached) {
        lat = cached.lat;
        lng = cached.lng;
      }
    }

    if (lat == null || lng == null) continue;

    const { address, isFullAddress } = buildFullAddress(client);
    const name = client.business_name ||
      [client.first_name, client.last_name].filter(Boolean).join(' ') ||
      'Lead senza nome';

    result.push({
      id: client.id,
      lat,
      lng,
      name,
      address,
      phone: client.phone || undefined,
      hasFullAddress: isFullAddress,
    });
  }

  return result;
}

/**
 * Geocodifica un indirizzo a partire dalle sue parti.
 * Usato al salvataggio di un lead per pre-calcolare le coordinate.
 * Ritorna { latitude, longitude, geocoded_at } oppure null se non riesce.
 */
export async function geocodeAddress(parts: {
  street_address?: string | null;
  street_number?: string | null;
  city?: string | null;
  province?: string | null;
  postal_code?: string | null;
  country?: string | null;
}): Promise<{ latitude: number; longitude: number; geocoded_at: string } | null> {
  // Serve almeno una città o un paese
  if (!parts.city?.trim() && !parts.country?.trim()) return null;

  // Verifica che Google Maps sia caricato
  if (!(window as any).google?.maps?.Geocoder) return null;

  const { address } = buildFullAddress(parts as any);
  if (!address) return null;

  try {
    const coords = await new Promise<{ lat: number; lng: number } | null>(resolve => {
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ address }, (results, status) => {
        if (status === 'OK' && results?.[0]) {
          const loc = results[0].geometry.location;
          resolve({ lat: loc.lat(), lng: loc.lng() });
        } else {
          resolve(null);
        }
      });
    });

    if (!coords) return null;

    return {
      latitude: coords.lat,
      longitude: coords.lng,
      geocoded_at: new Date().toISOString(),
    };
  } catch {
    return null;
  }
}

/**
 * Svuota la cache di geocodifica (per debug/reset)
 */
export function clearGeocodeCache(): void {
  geocodeCache.clear();
}
